import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

export interface VariableDefinition {
  key: string;
  default?: string;
}

/**
 * Extracts all {{variable}} or {{variable:default}} placeholders from content.
 * Supports Unicode characters (German umlaute, etc.).
 */
export function extractVariableDefinitions(content: string): VariableDefinition[] {
  const regex = /\{\{\s*([\p{L}\p{N}_-]+)\s*(?::\s*([^}]+?)\s*)?\}\}/gu;
  const seenLower = new Set<string>();
  const defs: VariableDefinition[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    const key = match[1].trim();
    const normalized = key.toLowerCase();
    const defaultValue = match[2]?.trim() || '';
    if (key && !seenLower.has(normalized)) {
      seenLower.add(normalized);
      defs.push({ key, default: defaultValue });
    }
  }
  return defs;
}

/**
 * Extracts all {{variable}} placeholders from a prompt content string.
 * Returns an array of unique variable names in the order they appear.
 * Deduplicates case-insensitively: {{Thema}} and {{thema}} count as one variable.
 */
export function extractVariables(content: string): string[] {
  return extractVariableDefinitions(content).map((d) => d.key);
}

/**
 * Replaces all {{variable}} placeholders in content with the provided values.
 * Matching is case-insensitive.
 * If a value is empty or not provided, falls back to the default value if present,
 * or preserves the placeholder in the output text.
 */
export function substituteVariables(content: string, values: Record<string, string>): string {
  const lowerMap: Record<string, string> = {};
  for (const [k, v] of Object.entries(values || {})) {
    if (v !== undefined && v !== null && v.trim() !== '') {
      lowerMap[k.toLowerCase()] = v;
    }
  }
  return content.replace(/\{\{\s*([\p{L}\p{N}_-]+)\s*(?::\s*([^}]+?)\s*)?\}\}/gu, (match, key, defaultValue) => {
    const trimmed = key.trim();
    const val = lowerMap[trimmed.toLowerCase()];
    if (val !== undefined) {
      return val;
    }
    if (defaultValue !== undefined && defaultValue.trim() !== '') {
      return defaultValue.trim();
    }
    return match;
  });
}

export interface VariableSubstitutionModalProps {
  promptName?: string;
  content: string;
  onClose: () => void;
  /** Called with the fully substituted text ready to be copied */
  onCopy: (substitutedContent: string) => void;
  initialValues?: Record<string, string>;
  onValuesChange?: (values: Record<string, string>) => void;
  copyMode?: 'normal' | 'english';
}

const VariableSubstitutionModal: React.FC<VariableSubstitutionModalProps> = ({
  promptName,
  content,
  onClose,
  onCopy,
  initialValues,
  onValuesChange,
  copyMode = 'normal',
}) => {
  const variableDefs = useMemo(() => extractVariableDefinitions(content), [content]);
  const variables = useMemo(() => variableDefs.map((v) => v.key), [variableDefs]);

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const v of variableDefs) {
      initial[v.key] = initialValues?.[v.key] ?? initialValues?.[v.key.toLowerCase()] ?? '';
    }
    return initial;
  });

  const [copied, setCopied] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount
  useEffect(() => {
    const timer = setTimeout(() => firstInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = useCallback(
    (variable: string, value: string) => {
      setValues((prev) => {
        const next = { ...prev, [variable]: value };
        onValuesChange?.(next);
        return next;
      });
    },
    [onValuesChange]
  );

  const handleCopy = useCallback(() => {
    const substituted = substituteVariables(content, values);
    onCopy(substituted);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      onClose();
    }, 1400);
  }, [content, values, onCopy, onClose]);

  // Allow Tab to submit on last field
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, _isLast: boolean) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleCopy();
      }
    },
    [handleCopy]
  );

  // Preview with current substitutions
  const previewContent = substituteVariables(content, values);
  const unfilledCount = variableDefs.filter(
    (v) => !values[v.key]?.trim() && !v.default
  ).length;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-fade-in-fast"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vsm-title"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/40 w-full max-w-lg max-h-[90vh] flex flex-col animate-scale-fade-in overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span
                className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 font-mono text-[12px] font-semibold bg-kind-prompt/10 text-kind-prompt"
                aria-hidden="true"
              >
                {'{}'}
              </span>
              <h2
                id="vsm-title"
                className="text-base font-bold text-gray-900 dark:text-gray-100 truncate"
              >
                {copyMode === 'english'
                  ? 'Platzhalter ausfüllen & auf Englisch kopieren'
                  : 'Platzhalter ausfüllen & kopieren'}
              </h2>
            </div>
            {promptName && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate pl-9">
                {promptName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-200 transition-all shrink-0"
            aria-label="Modal schließen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Variables form */}
        <div className="px-6 py-4 overflow-y-auto flex-1 scrollbar-thin">
          {variables.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
              Keine Variablen gefunden. Der Prompt wird direkt kopiert.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Füllen Sie die Variablen aus, bevor Sie den Prompt kopieren:
              </p>
              <div className="space-y-4">
                {variableDefs.map((vDef, idx) => {
                  const variable = vDef.key;
                  return (
                    <div key={variable}>
                      <label
                        htmlFor={`vsm-field-${idx}`}
                        className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
                      >
                        {variable}
                        <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500 font-mono">
                          {`{{${variable}${vDef.default ? ':' + vDef.default : ''}}}`}
                        </span>
                        {vDef.default && (
                          <span className="ml-2 text-[10px] text-gray-400 italic">
                            Standard: {vDef.default}
                          </span>
                        )}
                      </label>
                      <input
                        id={`vsm-field-${idx}`}
                        ref={idx === 0 ? firstInputRef : undefined}
                        type="text"
                        value={values[variable] || ''}
                        onChange={(e) => handleChange(variable, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, idx === variableDefs.length - 1)}
                        placeholder={vDef.default ? `Standard: ${vDef.default}` : `Wert für ${variable} eingeben…`}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-all"
                        style={{ boxShadow: 'none' }}
                        aria-required="false"
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Preview (collapsible) */}
          {variables.length > 0 && (
            <details className="mt-5 group">
              <summary className="text-xs font-semibold text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center gap-1">
                <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                Vorschau
              </summary>
              <div className="mt-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/40 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto scrollbar-thin font-mono">
                {previewContent}
              </div>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100/80 dark:bg-gray-800/60 hover:bg-gray-200/80 dark:hover:bg-gray-700/60 transition-all active:scale-95"
          >
            Abbrechen
          </button>

          <div className="flex items-center gap-2">
            {unfilledCount > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 hidden sm:inline">
                {unfilledCount === 1
                  ? '1 Feld noch leer'
                  : `${unfilledCount} Felder noch leer`}
              </span>
            )}
            <button
              onClick={handleCopy}
              className={`btn btn-primary ${copied ? '!bg-emerald-700 !border-emerald-700' : ''}`}
              aria-label={copyMode === 'english' ? 'Übersetzen und kopieren' : 'Prompt in Zwischenablage kopieren'}
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Kopiert!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copyMode === 'english' ? 'Übersetzen & Kopieren' : 'Prompt kopieren'}
                  <span className="hidden sm:inline text-indigo-200 text-xs font-normal ml-0.5">(⌘↵)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VariableSubstitutionModal;
